import { css, styled } from 'styled-components';

import { theme } from '../../../styles';
import { CheckmarkIcon } from '../../../assets/icons';
import { useTabbing } from '../../../utils';
import { useMediaMin } from '../../Media';
import { Text } from '../../Text';

const StyledInputLabel = styled.label<{ disabled?: boolean; size: string; topAlignCheckbox?: boolean }>`
  display: inline-flex;
  align-items: ${props => (props.topAlignCheckbox ? 'start' : 'center')};
  cursor: pointer;
  padding: 0.25rem 0;
  ${props =>
    props.disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.7;
    `}
  * > button {
    ${props => props.size === 'small' && theme.typography.textSmall};
    ${props => props.size === 'standard' && theme.typography.textStandard};
    ${props => props.size === 'medium' && theme.typography.textMedium};
    ${props => props.size === 'large' && theme.typography.textLarge};
  }
`;

const StyledOuterBox = styled.div<{
  disabled?: boolean;
  isDesktop: boolean;
  hasError?: boolean;
  topAlignCheckbox?: boolean;
}>`
  position: relative;
  box-sizing: border-box;
  margin-right: 0.625rem;
  margin-top: ${props => (props.topAlignCheckbox ? '0.1rem' : '0')};
  min-width: ${props => (props.isDesktop ? 1.25 : 1.875)}rem;
  min-height: ${props => (props.isDesktop ? 1.25 : 1.875)}rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
  background-color: ${props => (props.hasError ? theme.colors.babyPink : theme.colors.white)};
  border: 1px solid
    ${props => (props.hasError ? theme.colors.burgundy : props.disabled ? theme.colors.black30 : theme.colors.black)};
  cursor: pointer;
  ${props =>
    props.disabled &&
    css`
      cursor: not-allowed;
    `}
`;

const StyledCheckboxInput = styled.input<{ isTabbing: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  margin: 0rem;
  width: 100%;
  height: 100%;
  cursor: inherit;
  border: none;
  border-radius: 0;
  appearance: none !important;
  outline: none;

  ${props =>
    props.isTabbing &&
    css`
      &:focus {
        outline: ${theme.colors.darkBlue} solid 2px;
        outline-offset: 5px;
      }
    `}
`;

type CheckboxProps = {
  id: string;
  checked: boolean;
  hasError?: boolean;
  disabled?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  children: React.ReactNode;
  size?: 'small' | 'standard' | 'medium' | 'large';
  topAlignCheckbox?: boolean;
};

export const Checkbox = ({
  id,
  checked,
  hasError,
  disabled,
  onChange,
  size = 'standard',
  topAlignCheckbox,
  children,
}: CheckboxProps) => {
  const isDesktop = useMediaMin(theme.breakpoint.lg);
  const isTabbing = useTabbing();

  return (
    <StyledInputLabel
      data-testid={`test-${id}`}
      htmlFor={id}
      size={size}
      disabled={disabled}
      topAlignCheckbox={topAlignCheckbox}
    >
      <StyledOuterBox isDesktop={isDesktop} hasError={hasError} disabled={disabled} topAlignCheckbox={topAlignCheckbox}>
        {checked && <CheckmarkIcon color={disabled ? 'black30' : 'black'} width={isDesktop ? 12 : 18} />}
        <StyledCheckboxInput
          id={id}
          data-testid={`test-${id}-input`}
          name={`checkbox-${id}`}
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          isTabbing={isTabbing}
        />
      </StyledOuterBox>
      <Text renderAs="span" size={size}>
        {children}
      </Text>
    </StyledInputLabel>
  );
};
